import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';

import { getPlaces } from '../../../../api/services/Locations';
import * as Constants from '../../../../constants';

interface IUsePlaces {
  places: Place[];
  debounceHandleQuery: (query: string) => void;
  isFetching: boolean;
}

export const usePlaces = (initialValues?: Place): IUsePlaces => {
  const [queryParams, setQueryParams] = useState<QueryParams>({
    page: 1,
    perPage: Constants.ITEM_LIMIT_PER_PAGE,
    query: undefined,
  });
  const [places, setPlaces] = useState<Place[]>(initialValues ? [initialValues] : []);
  const [isFetching, setIsFetching] = useState(false);
  const abortPlacesController = useRef<AbortController>();

  const getQueryParams = useCallback(() => {
    const queryParamsToSubmit = {
      perPage: Constants.ITEM_LIMIT_PER_PAGE,
      page: queryParams.page,
      q: queryParams.query,
    };

    return queryParamsToSubmit;
  }, [queryParams]);

  const debounceHandleQuery = useMemo(() => debounce(handleQuery, Constants.DEBOUNCE_TIME), []);

  function handleQuery(query) {
    if (!query) {
      setPlaces(initialValues ? [initialValues] : []);
      setQueryParams((prevQueryParams) => ({
        query: undefined,
        page: 1,
        perPage: prevQueryParams.perPage,
      }));
    }

    if (query !== queryParams.query && query.length >= Constants.MINIMUM_SEARCH_STRING_LENGTH) {
      setQueryParams((prevQueryParams) => ({
        query: query,
        page: 1,
        perPage: prevQueryParams.perPage,
      }));
    }
  }

  const fetchPlaces = useCallback(async () => {
    try {
      const queryParamsUpdated = getQueryParams();

      if (!queryParamsUpdated.q) {
        return;
      }
      setIsFetching(true);
      abortPlacesController.current?.abort();
      abortPlacesController.current = new AbortController();
      const response = await getPlaces(queryParamsUpdated, abortPlacesController.current.signal);

      if (response?.type === 'success') {
        const newPlaces = response.data.locations;

        if (newPlaces?.length > 0) {
          setIsFetching(false);
          setPlaces((prevPlaces) => (queryParamsUpdated.page === 1 ? newPlaces : prevPlaces.concat(newPlaces)));
        } else if (!abortPlacesController.current?.signal.aborted) {
          setIsFetching(false);
        }
      }
    } catch (error) {}
  }, [getQueryParams]);

  useEffect(() => {
    return () => {
      debounceHandleQuery.cancel();
    };
  }, [queryParams.query]);

  useEffect(() => {
    return () => {
      abortPlacesController.current?.abort();
    };
  }, []);

  useEffect(() => {
    fetchPlaces();
  }, [fetchPlaces]);

  return { places, debounceHandleQuery, isFetching };
};
